/* ===================================================================
   useSmsOptIn() + useSmsOptInActions() -- the text opt-in funnel.

   Binds GET /api/v1/sms/opt-in/customers plus the two single-customer
   mutations (engine/api/v1/sms_optin.py). Same gating pattern as the other
   data hooks: disabled until a Bearer rr_ token exists, so it never fires a
   guaranteed 401 on first paint.

   HONEST STATE: everything the owner reads (the stage, the headline, the
   explanation) comes from the server verbatim. Nothing here invents a reason
   a customer cannot be texted, and nothing here caches an opt-in optimistically
   -- a consent record that the server did not confirm must never render as
   though it landed.

   NO BULK ACTION, deliberately. There is no bulk route to bind to; the
   confirmation text goes to one customer at a time because only that customer
   checked the box.
   =================================================================== */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  api,
  type OptInOverview,
  type RecordOptInInput,
  type RecordOptInResult,
  type SendConfirmationResult,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useSmsOptIn(enabled = true): UseQueryResult<OptInOverview, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.smsOptIn(),
    queryFn: ({ signal }) => api.getOptInOverview(signal),
    enabled: hasToken && enabled,
    staleTime: 30_000,
  });
}

export interface UseSmsOptInActionsResult {
  record: (customerId: string, input: RecordOptInInput) => Promise<RecordOptInResult>;
  sendConfirmation: (customerId: string) => Promise<SendConfirmationResult>;
  recording: boolean;
  sending: boolean;
}

export function useSmsOptInActions(): UseSmsOptInActionsResult {
  const qc = useQueryClient();

  const recordM = useMutation({
    mutationFn: ({ customerId, input }: { customerId: string; input: RecordOptInInput }) =>
      api.recordOptIn(customerId, input),
    // Refetch rather than patch the cache: the funnel stage is derived
    // server-side from BOTH the capture row and the consent record, so the
    // server's answer is the only trustworthy one.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.smsOptIn() });
      void qc.invalidateQueries({ queryKey: qk.customers() });
    },
  });

  const sendM = useMutation({
    mutationFn: (customerId: string) => api.sendOptInConfirmation(customerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.smsOptIn() });
    },
  });

  return {
    record: (customerId, input) => recordM.mutateAsync({ customerId, input }),
    sendConfirmation: (customerId) => sendM.mutateAsync(customerId),
    recording: recordM.isPending,
    sending: sendM.isPending,
  };
}
